import dayjs from "dayjs";
import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import Header from "../../../components/Header";
import LoadingOverlay from "../../../components/LoadingOverlay";
import ReportFiltersCard, {
    ReportFilterField,
} from "../../../components/reports/ReportFiltersCard";
import Sidebar from "../../../components/Sidebar";
import { getEmployeeProductionReport } from "../../../services/analytics/getEmployeeProductionReport";
import { getServiceOverviewFilters } from "../../../services/analytics/getServiceOverviewFilters";
import EmployeeProductionTable from "./components/EmployeeProductionTable";
import {
    buildEmployeeProductionFilters,
    buildEmployeeServicesUrl,
    buildInitialSelectedFilters,
    getInitialEmployeeProductionDateRange,
} from "./employeeProductionFilters";

const DATE_RANGE_PRESETS = {
    Today: () => [dayjs(), dayjs()],
    Yesterday: () => [
        dayjs().subtract(1, "day"),
        dayjs().subtract(1, "day"),
    ],
    "This Month to Date": () => [dayjs().startOf("month"), dayjs()],
    "This Month": () => [dayjs().startOf("month"), dayjs().endOf("month")],
    "This Year to Date": () => [dayjs().startOf("year"), dayjs()],
    "Last Year": () => [
        dayjs().subtract(1, "year").startOf("year"),
        dayjs().subtract(1, "year").endOf("year"),
    ],
};

const DATE_RANGE_OPTIONS = [
    ...Object.keys(DATE_RANGE_PRESETS),
    "Custom",
];

const FILTER_DISPLAY_ORDER = [
    "services",
    "employees",
    "departments",
    "serviceYears",
];

const getFilterDisplayOrder = (key) => {
    const index = FILTER_DISPLAY_ORDER.indexOf(key);
    return index === -1 ? FILTER_DISPLAY_ORDER.length : index;
};

const getMatchingRangeType = (dateRange) => {
    const [startDate, endDate] = dateRange ?? [];

    const matchedRangeType = Object.entries(DATE_RANGE_PRESETS).find(
        ([, getRange]) => {
            const [presetStartDate, presetEndDate] = getRange();

            return (
                presetStartDate.isSame(startDate, "day") &&
                presetEndDate.isSame(endDate, "day")
            );
        },
    );

    return matchedRangeType ? matchedRangeType[0] : "Custom";
};

export default function EmployeeProductionReport() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [filters, setFilters] = useState({});
    const [selectedDateRange, setSelectedDateRange] = useState(
        getInitialEmployeeProductionDateRange,
    );
    const [appliedDateRange, setAppliedDateRange] = useState(
        getInitialEmployeeProductionDateRange,
    );
    const [selectedRangeType, setSelectedRangeType] = useState("This Month");
    const [selectedFilters, setSelectedFilters] = useState({});
    const [appliedSelectedFilters, setAppliedSelectedFilters] = useState({});
    const [employeeProductionData, setEmployeeProductionData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");
    const [catalogsReady, setCatalogsReady] = useState(false);
    const resultsSectionRef = useRef(null);
    const shouldScrollToResultsRef = useRef(false);

    const reportFilters = useMemo(
        () =>
            buildEmployeeProductionFilters({
                selectedDateRange: appliedDateRange,
                selectedFilters: appliedSelectedFilters,
            }),
        [appliedDateRange, appliedSelectedFilters],
    );

    const onClickNumberOfServices = (employeeId, status) => {
        const detailUrl = buildEmployeeServicesUrl(
            buildEmployeeProductionFilters({
                selectedDateRange: appliedDateRange,
                selectedFilters: appliedSelectedFilters,
                employeeId,
                status,
            }),
        );

        window.open(detailUrl, "_blank");
    };

    useEffect(() => {
        let isMounted = true;

        const getFilters = async () => {
            try {
                setIsLoading(true);
                setError("");
                const response = await getServiceOverviewFilters();

                if (!response?.data?.success) {
                    throw new Error(
                        response?.data?.message ||
                            response?.message ||
                            "Unable to load Employee Production filters.",
                    );
                }

                if (!isMounted) return;
                const newFilters = response.data.serviceOverViewFilters;
                const initialSelectedFilters =
                    buildInitialSelectedFilters(newFilters);
                setFilters(newFilters);
                setSelectedFilters((prev) =>
                    Object.keys(prev).length > 0
                        ? { ...initialSelectedFilters, ...prev }
                        : initialSelectedFilters,
                );
                setAppliedSelectedFilters((prev) =>
                    Object.keys(prev).length > 0 ? prev : initialSelectedFilters,
                );
                setCatalogsReady(true);
            } catch (requestError) {
                if (!isMounted) return;
                setEmployeeProductionData(null);
                setError(
                    requestError?.response?.data?.message ||
                        requestError?.message ||
                        "Unable to load Employee Production filters.",
                );
                setIsLoading(false);
            }
        };

        getFilters();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!catalogsReady) return undefined;

        let isMounted = true;

        const fetchEmployeeProductionReport = async () => {
            setIsLoading(true);
            setError("");
            setEmployeeProductionData(null);

            try {
                const response = await getEmployeeProductionReport(reportFilters);

                if (!isMounted) return;
                if (response?.success) {
                    setEmployeeProductionData(
                        response.employeesProduction || [],
                    );
                } else {
                    setError(
                        response?.message ||
                            "Unable to load Employee Production Report.",
                    );
                }
            } catch (requestError) {
                if (!isMounted) return;
                setError(
                    requestError?.response?.data?.message ||
                        "Unable to load Employee Production Report.",
                );
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchEmployeeProductionReport();

        return () => {
            isMounted = false;
        };
    }, [catalogsReady, reportFilters]);

    const handleFilterChange = (key, value) => {
        setSelectedFilters((prev) => ({ ...prev, [key]: value }));
    };

    const onRangeDatePickerChange = (newDateRange) => {
        setSelectedDateRange(newDateRange);
    };

    const handleRangeTypeChange = (value) => {
        setSelectedRangeType(value);

        if (value === "Custom") {
            return;
        }

        const nextDateRange = DATE_RANGE_PRESETS[value]();
        onRangeDatePickerChange(nextDateRange);
    };

    const handleDateInputChange = (index, value) => {
        const nextDate = value ? dayjs(value) : dayjs();

        const nextDateRange = selectedDateRange.map((date, dateIndex) =>
                dateIndex === index ? nextDate : date,
        );
        onRangeDatePickerChange(nextDateRange);
        setSelectedRangeType(getMatchingRangeType(nextDateRange));
    };

    const handleApplyFilters = () => {
        shouldScrollToResultsRef.current = true;
        setAppliedDateRange([...selectedDateRange]);
        setAppliedSelectedFilters({ ...selectedFilters });
    };

    useEffect(() => {
        if (isLoading || !shouldScrollToResultsRef.current) {
            return;
        }

        if (resultsSectionRef.current) {
            resultsSectionRef.current.scrollIntoView?.({
                behavior: "smooth",
                block: "start",
            });
        }

        shouldScrollToResultsRef.current = false;
    }, [employeeProductionData, isLoading]);

    return (
        <section>
            <LoadingOverlay isLoading={isLoading} />
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                        <div className="flex flex-col items-start max-w-3xl">
                            <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                Employee Production
                            </h1>
                        </div>

                        <ReportFiltersCard fieldsClassName="flex flex-col gap-5">
                            <div
                                className="grid grid-cols-1 gap-4 md:grid-cols-3"
                                data-testid="employee-production-date-filters"
                            >
                                <ReportFilterField label="Date Range">
                                    <select
                                        value={selectedRangeType}
                                        onChange={(event) =>
                                            handleRangeTypeChange(event.target.value)
                                        }
                                        disabled={isLoading}
                                        className="w-full border border-gray-300 rounded-md form-input disabled:bg-slate-100 disabled:text-slate-500"
                                    >
                                        {DATE_RANGE_OPTIONS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </select>
                                </ReportFilterField>

                                <ReportFilterField label="Start Date">
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded-md form-input"
                                        value={selectedDateRange[0]?.format("YYYY-MM-DD")}
                                        onChange={(event) =>
                                            handleDateInputChange(0, event.target.value)
                                        }
                                        disabled={isLoading}
                                    />
                                </ReportFilterField>

                                <ReportFilterField label="End Date">
                                    <input
                                        type="date"
                                        className="w-full border border-gray-300 rounded-md form-input"
                                        value={selectedDateRange[1]?.format("YYYY-MM-DD")}
                                        onChange={(event) =>
                                            handleDateInputChange(1, event.target.value)
                                        }
                                        disabled={isLoading}
                                    />
                                </ReportFilterField>
                            </div>

                            <div
                                className="flex flex-col gap-4 xl:flex-row xl:items-end"
                                data-testid="employee-production-report-filters"
                            >
                                <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
                                    {Object.entries(filters)
                                        .sort(
                                            ([firstKey], [secondKey]) =>
                                                getFilterDisplayOrder(firstKey) -
                                                getFilterDisplayOrder(secondKey),
                                        )
                                        .map(([key, options]) => (
                                            <ReportFilterField
                                                key={key}
                                                label={
                                                    key === "serviceYears"
                                                        ? "Year"
                                                        : key[0].toUpperCase() +
                                                          key.substring(1)
                                                }
                                            >
                                                <Select
                                                    options={options}
                                                    value={selectedFilters[key] || null}
                                                    onChange={(option) =>
                                                        handleFilterChange(key, option)
                                                    }
                                                    classNamePrefix="select"
                                                    isDisabled={isLoading}
                                                />
                                            </ReportFilterField>
                                        ))}
                                </div>

                                <div className="flex justify-end xl:shrink-0">
                                    <button
                                        type="button"
                                        onClick={handleApplyFilters}
                                        disabled={isLoading}
                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white transition bg-teal-500 rounded-lg shadow-sm hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        Apply Filters
                                    </button>
                                </div>
                            </div>
                        </ReportFiltersCard>

                        {error && (
                            <div className="p-3 mt-6 text-sm text-red-700 border border-red-200 rounded-lg bg-red-50">
                                {error}
                            </div>
                        )}

                        <div ref={resultsSectionRef} className="mt-6">
                            {employeeProductionData && (
                                <EmployeeProductionTable
                                    data={employeeProductionData}
                                    onClickNumberOfServices={
                                        onClickNumberOfServices
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
